import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"

// The DropzoneController handles file uploads via drag & drop or file input
// It supports:
// - Drag and drop file uploads with visual feedback
// - File size validation (configurable max size)
// - Multiple file upload handling
// - Upload progress tracking
// - Error handling and user feedback
// - File removal

export default class extends Controller {
  static targets = ["input", "preview", "dropZone"]
  static values = {
    maxFileSize: Number,
    acceptedFiles: String
  }

  connect() {
    if (!this.hasdropZoneTarget) {
      this.dropZone = this.element
    }
    this.bindEvents()
    this.pendingUploads = 0
    this.blobIds = new Set()
  }

  disconnect() {
    this.unbindEvents()
  }

  bindEvents() {
    if (!this.dropZone) return

    // Prevent default drag behaviors
    const preventDefaults = (e) => {
      e.preventDefault()
      e.stopPropagation()
    }

    // Only bind events to the dropzone element
    ;['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
      this.dropZone.addEventListener(eventName, preventDefaults, false)
    })

    // Handle drop zone highlighting
    ;['dragenter', 'dragover'].forEach(eventName => {
      this.dropZone.addEventListener(eventName, this.highlight.bind(this), false)
    })

    ;['dragleave', 'drop'].forEach(eventName => {
      this.dropZone.addEventListener(eventName, this.unhighlight.bind(this), false)
    })

    // Handle dropped files
    this.dropZone.addEventListener('drop', this.handleDrop.bind(this), false)
  }

  unbindEvents() {
    if (!this.dropZone) return

    const preventDefaults = (e) => {
      e.preventDefault()
      e.stopPropagation()
    }

    ;['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
      this.dropZone.removeEventListener(eventName, preventDefaults, false)
    })
  }

  highlight(e) {
    this.dropZone.classList.add('border-blue-400')
  }

  unhighlight(e) {
    this.dropZone.classList.remove('border-blue-400')
  }

  handleDrop(e) {
    const dt = e.dataTransfer
    if (dt.files?.length > 0) {
      Array.from(dt.files).forEach(file => this.uploadFile(file))
    }
  }

  handleChange(e) {
    if (this.inputTarget.files?.length > 0) {
      Array.from(this.inputTarget.files).forEach(file => this.uploadFile(file))
      // Clear input to allow uploading same file again
      this.inputTarget.value = null
    }
  }

  validateFile(file) {
    if (file.size > this.maxFileSizeValue * 1024 * 1024) {
      this.showError(`File too large. Maximum size is ${this.maxFileSizeValue}MB`)
      return false
    }

    const acceptedTypes = this.acceptedFilesValue.split(',').map(type => type.trim())
    if (!acceptedTypes.some(type => file.type.match(new RegExp(type.replace('*', '.*'))))) {
      this.showError(`Invalid file type. Accepted types are: ${this.acceptedFilesValue}`)
      return false
    }

    return true
  }

  uploadFile(file) {
    if (!this.validateFile(file)) return

    // Create progress element
    const progressElement = this.createProgressElement(file)
    this.previewTarget.insertAdjacentElement('afterbegin', progressElement)

    const url = this.inputTarget.dataset.directUploadUrl
    const upload = new DirectUpload(file, url, this)

    this.pendingUploads++

    upload.create((error, blob) => {
      if (error) {
        this.showError(`Error uploading file: ${error}`)
        progressElement.remove()
        this.pendingUploads--
      } else {
        const form = this.element.closest('form')
        if (!form) {
          this.showError('No form found for file upload')
          progressElement.remove()
          this.pendingUploads--
          return
        }

        // Store the blob ID
        this.blobIds.add(blob.signed_id)

        // Update or create the hidden input with all blob IDs
        this.updateHiddenInput(form)

        // Keep progress element until Turbo Stream updates the preview
        progressElement.classList.add('uploading')

        this.pendingUploads--

        // Only submit the form when all uploads are complete
        if (this.pendingUploads === 0) {
          // Listen for Turbo Stream update completion
          const observer = new MutationObserver((mutations, obs) => {
            const previewUpdated = mutations.some(mutation => 
              mutation.type === 'childList' && 
              mutation.target === this.previewTarget
            )
            
            if (previewUpdated) {
              // Remove all temporary progress elements
              this.previewTarget.querySelectorAll('.uploading').forEach(el => el.remove())
              obs.disconnect()
              
              // Clear the blob IDs after successful upload
              this.blobIds.clear()
            }
          })

          // Start observing preview target for changes
          observer.observe(this.previewTarget, {
            childList: true,
            subtree: true
          })

          // Submit form using Turbo
          if (typeof Turbo !== 'undefined') {
            Turbo.navigator.submitForm(form)
          } else {
            form.requestSubmit()
          }
        }
      }
    })
  }

  updateHiddenInput(form) {
    const inputName = this.inputTarget.name
    let hiddenInput = form.querySelector(`input[name="${inputName}"][type="hidden"]`)
    
    if (!hiddenInput) {
      hiddenInput = document.createElement('input')
      hiddenInput.setAttribute('type', 'hidden')
      hiddenInput.setAttribute('name', inputName)
      form.appendChild(hiddenInput)
    }

    // Join all blob IDs with a comma
    hiddenInput.value = Array.from(this.blobIds).join(',')
  }

  // DirectUpload delegate for progress tracking
  directUploadWillStoreFileWithXHR(xhr) {
    xhr.upload.addEventListener("progress", event => {
      const progress = event.loaded / event.total * 100
      this.updateProgress(xhr.id, progress)
    })
  }

  // Helper methods
  createProgressElement(file) {
    const div = document.createElement('div')
    div.className = 'my-2 p-2 bg-theme-primary rounded shadow-sm border border-gray-200'
    div.innerHTML = `
      <div class="flex items-center justify-between">
        <div class="flex items-center space-x-2">
          <svg class="h-5 w-5 text-theme-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
          <span class="text-sm font-medium text-theme-text">${file.name}</span>
          <span class="text-xs text-theme-text">${this.formatFileSize(file.size)}</span>
        </div>
      </div>
      <div class="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden">
        <div class="h-2 bg-purple-600 rounded-full transition-all duration-150" style="width: 0%" data-upload-progress></div>
      </div>
    `
    return div
  }

  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  updateProgress(id, progress) {
    const progressBar = this.previewTarget.querySelector('[data-upload-progress]')
    if (progressBar) {
      progressBar.style.width = `${progress}%`
    }
  }

  showError(message) {
    const event = new CustomEvent('flash', {
      detail: { type: 'error', message }
    })
    window.dispatchEvent(event)
  }
}